import React, { useEffect, useId, useState } from 'react';
import SlidePanel from '../SlidePanel';
import PeoplePicker from '../settings/PeoplePicker';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { KudosOptions } from './types';
import { KUDOS_EMOJI, NOTE_MAX } from './kudos';

const FIELD =
  'mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100';

// "Give kudos": a team or a person, an optional project, one of six emoji and a short note. The people who
// receive it see it in their Updates. Nothing is counted.
export default function GiveKudosPanel({ isOpen, onClose, onGiven, initialProject = null }: { isOpen: boolean; onClose: () => void; onGiven: () => void; /** The project to name when the panel opens (a project row's "Give kudos"); the person can change or clear it. */ initialProject?: string | null }) {
  const { showToast } = useToast();
  const ids = { team: useId(), project: useId(), note: useId() };
  const [options, setOptions] = useState<KudosOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [kind, setKind] = useState<'team' | 'person'>('team');
  const [teamId, setTeamId] = useState('');
  const [person, setPerson] = useState<string[]>([]);
  const [projectKey, setProjectKey] = useState('');
  const [emoji, setEmoji] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  // The choices are read when the panel opens, so a new team or person is there without reloading Home.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadError(null);
    apiService
      .getKudosOptions()
      .then((r) => {
        if (cancelled) return;
        if (r.success) setOptions(r.data);
        else setLoadError(r.error || 'Could not load the teams and people');
      })
      .catch((e: any) => !cancelled && setLoadError(e?.response?.data?.error || 'Could not load the teams and people'));
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Opening from a project's row names that project; opening from the Kudos block names none.
  useEffect(() => {
    if (isOpen) setProjectKey(initialProject ?? '');
  }, [isOpen, initialProject]);

  const reset = () => {
    setKind('team');
    setTeamId('');
    setPerson([]);
    setProjectKey('');
    setEmoji('');
    setNote('');
  };

  const recipientName = kind === 'team' ? options?.teams.find((t) => String(t.id) === teamId)?.name : options?.people.find((p) => p.account_id === person[0])?.display_name;
  const ready = !!emoji && (kind === 'team' ? !!teamId : person.length === 1);

  const send = async () => {
    if (!ready) return;
    setSending(true);
    try {
      const r = await apiService.giveKudos({
        to_kind: kind,
        ...(kind === 'team' ? { to_team_id: Number(teamId) } : { to_account_id: person[0] }),
        project_key: projectKey || null,
        emoji,
        note: note.trim() || null,
      });
      if (!r.success) throw new Error(r.error || 'Could not send the kudos');
      showToast('success', `Kudos sent to ${kind === 'team' ? (/\bteam\b/i.test(recipientName ?? '') ? `the ${recipientName}` : `the ${recipientName} team`) : recipientName}`);
      reset();
      onGiven();
      onClose();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not send the kudos');
    } finally {
      setSending(false);
    }
  };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title="Give kudos" widthPercent={34} minWidthPx={380}>
      <div className="space-y-5 p-1">
        <p className="text-sm text-gray-700">Thank a team or a person. They see it in their Updates, and so do the people on the project you name.</p>

        {loadError && (
          <p role="alert" className="rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200">
            {loadError}
          </p>
        )}

        <fieldset>
          <legend className="text-sm font-medium text-gray-900">Give kudos to</legend>
          <div className="mt-2 inline-flex rounded-full bg-gray-100 p-0.5 text-sm" role="radiogroup" aria-label="A team or a person">
            {(['team', 'person'] as const).map((k) => (
              <label key={k} className="cursor-pointer">
                <input type="radio" name="kudos-kind" value={k} checked={kind === k} onChange={() => setKind(k)} className="peer sr-only" />
                <span className="block rounded-full px-4 py-1.5 text-gray-700 peer-checked:bg-white peer-checked:font-medium peer-checked:text-gray-900 peer-checked:shadow-sm peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500">
                  {k === 'team' ? 'A team' : 'A person'}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {kind === 'team' ? (
          <div>
            <label htmlFor={ids.team} className="block text-sm font-medium text-gray-900">
              Team
            </label>
            <select id={ids.team} value={teamId} onChange={(e) => setTeamId(e.target.value)} className={FIELD}>
              <option value="">Choose a team</option>
              {(options?.teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <PeoplePicker
            label="Person"
            people={(options?.people ?? []).map((p) => ({ account_id: p.account_id, display_name: p.display_name }))}
            value={person}
            onChange={setPerson}
            single
            emptyText="Nobody chosen yet."
          />
        )}

        <div>
          <label htmlFor={ids.project} className="block text-sm font-medium text-gray-900">
            Project (optional)
          </label>
          <select id={ids.project} value={projectKey} onChange={(e) => setProjectKey(e.target.value)} className={FIELD}>
            <option value="">No project</option>
            {(options?.projects ?? []).map((p) => (
              <option key={p.project_key} value={p.project_key}>
                {p.name ? `${p.name} (${p.project_key})` : p.project_key}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-600">Everyone on the project sees it, not only the person or team.</p>
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-gray-900">Emoji</legend>
          <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Emoji">
            {KUDOS_EMOJI.map((e) => (
              <label key={e.key} className="cursor-pointer" title={e.label}>
                <input type="radio" name="kudos-emoji" value={e.key} checked={emoji === e.key} onChange={() => setEmoji(e.key)} className="peer sr-only" />
                <span className="flex h-11 w-11 items-center justify-center rounded-xl text-2xl ring-1 ring-inset ring-gray-200 hover:bg-gray-50 peer-checked:bg-blue-50 peer-checked:ring-2 peer-checked:ring-blue-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500">
                  <span aria-hidden>{e.glyph}</span>
                  <span className="sr-only">{e.label}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label htmlFor={ids.note} className="block text-sm font-medium text-gray-900">
            Note (optional)
          </label>
          <textarea id={ids.note} value={note} onChange={(e) => setNote(e.target.value)} maxLength={NOTE_MAX} rows={3} className={FIELD} placeholder="What went well?" />
          <p className="mt-1 text-right text-xs text-gray-600" aria-live="polite">
            {note.length} of {NOTE_MAX}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={send}
            disabled={!ready || sending}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            {sending ? 'Sending…' : 'Send kudos'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </SlidePanel>
  );
}
